import {isEmpty} from '@shared/helpers/lang'

import {
    BannerTypeType,
    EasyCardItemType,
    PaymentsCardCodeType,
    PayMethod,
    PromotionCardCodeType,
    PromotionCardProductNameCodeType,
} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'
import {
    CardState,
    getCardEasyPaysFirstItem,
    getCommonInstallmentMinAmount,
    getCommonInstallments,
    setupLongInterestFreeInstallments,
    convertOverseaEasyCard,
    isApplicableCardProductCodes,
} from '@web/slices/card/slice'
import makeSlice from '@web/slices/card/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {CardEasyPay, OverseaCardEasyPay} from '../../apis/interface/response/interface'

const convertEasyCard = (cardEasyPays: CardEasyPay[]): CardState['cardEasyPay']['items'] => {
    return cardEasyPays.map((cardEasyPay) => {
        const companyCode = cardEasyPay.cardCompanyCode as PaymentsCardCodeType
        return {
            category: cardEasyPay.cardCategory,
            companyCode,
            companyName: `${getCardCodeName(companyCode)}카드`,
            companyId: cardEasyPay.cardCompanyId,
            id: cardEasyPay.cardId,
            imageUrl: cardEasyPay.cardImageUrl,
            name: cardEasyPay.cardName,
            ownerType: cardEasyPay.cardOwnerType,
            productCode: cardEasyPay.cardProductCode,
            type: cardEasyPay.cardType ?? 'CREDIT',
            maskedNumber: cardEasyPay.maskedCardNumber,
            payService: {
                payable: cardEasyPay.payServiceAvailable,
                maintenance: cardEasyPay.payServiceMaintenanceSchedule,
                supportedMerchant: true,
            },
            longTermInstallment: cardEasyPay.longTermInstallment,
            registeredDateTime: cardEasyPay.registeredDateTime,
            reward: !cardEasyPay.rewardCard
                ? null
                : {
                      rewardAccumulateRate: cardEasyPay.rewardAccumulateRate,
                      rewardPointResidualAmount: cardEasyPay.rewardPointResidualAmount,
                      minAmount: 0,
                  },
            interestFreeBenefitPromotion: {
                isAvailable: true,
                minAmount: 0,
                month: 0,
            },
            plcc: cardEasyPay.hyundaiPlccCard
                ? {
                      type: 'NAVER_HYUNDAI',
                  }
                : null,
            issuerCode: cardEasyPay.cardIssuerCode,
            baseInstallmentInfo: getCommonInstallments(cardEasyPay.cardCompanyCode),
        }
    })
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder.addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
            const {payMethod, banner} = payload

            state.cardEasyPay.bannerItemTypes =
                banner.bannerListsByExposureAreaType['EASY_PAY_CREDIT_CARD']?.reduce<
                    {
                        cardCompanyCode: PromotionCardCodeType
                        bannerType: BannerTypeType
                        cardProductNameCode: Maybe<PromotionCardProductNameCodeType>
                    }[]
                >((acc, {cardCompanyCode, bannerType}) => {
                    if (cardCompanyCode) {
                        acc.push({cardCompanyCode, bannerType, cardProductNameCode: null})
                    }
                    return acc
                }, []) ?? []

            state.partnership.companyCodes = payMethod.partnershipCardCodes ?? []
            // 카드 간편결제
            state.cardEasyPay.items = [
                ...convertEasyCard(payMethod.cardEasyPays),
                ...convertOverseaEasyCard(payMethod.overseasCardEasyPays),
            ]
            state.cardEasyPay.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth || null
            state.cardEasyPay.interestFreeInstallments = setupLongInterestFreeInstallments(
                payMethod.billingPayMethods.cardEasyPay?.interestFreeInstallments.map((info) => ({
                    ...info,
                    minAmount: getCommonInstallmentMinAmount(info.cardCompanyCode as PaymentsCardCodeType),
                })) ?? [],
                payMethod.billingPayMethods.cardEasyPay?.interestInstallments ?? [],
            )

            const firstItem = isEmpty(payMethod.overseasCardEasyPays)
                ? getCardEasyPaysFirstItem<CardEasyPay>(({payServiceAvailable}) => payServiceAvailable)(
                      payMethod.cardEasyPays,
                  )
                : getCardEasyPaysFirstItem<OverseaCardEasyPay>(
                      ({overseasCardStatusType}) => overseasCardStatusType === 'ACTIVE',
                  )(payMethod.overseasCardEasyPays)

            state.cardEasyPay.selected.id = firstItem?.cardId ?? ''
            state.cardEasyPay.selected.itemType = !firstItem ? EasyCardItemType.ADD_CARD : EasyCardItemType.CARD

            // 일반 카드결제
            state.creditCard.companies = payMethod.cardCompanies.map(({code, name}) => ({
                name,
                code: code as PaymentsCardCodeType,
                baseInstallmentInfo: getCommonInstallments(code),
            }))

            state.creditCard.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth || null
            state.creditCard.interestFreeInstallments = setupLongInterestFreeInstallments(
                payMethod.billingPayMethods.card?.interestFreeInstallments.map((info) => ({
                    ...info,
                    minAmount: getCommonInstallmentMinAmount(info.cardCompanyCode as PaymentsCardCodeType),
                })) ?? [],
                // @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2754#issuecomment-2145863
                payMethod.billingPayMethods.cardEasyPay?.interestInstallments?.filter(({applicableCardProductCode}) => {
                    return isApplicableCardProductCodes(applicableCardProductCode ?? '')
                }) ?? [],
            )

            state.creditCardBillDiscounts = payMethod.creditCardBillDiscounts.map((billDiscount) => ({
                benefit: billDiscount.benefit,
                cardCompanyCode: billDiscount.corporationCode as PaymentsCardCodeType,
                discountRate: billDiscount.discountRate,
                startDate: billDiscount.startDate,
                endDate: billDiscount.endDate,
                linkUrl: billDiscount.linkUrl,
                additionDescription: billDiscount.orderAdditionDescription,
                detailDescription: billDiscount.orderDetailDescription,
                rank: billDiscount.rank,
                eventType: billDiscount.eventType,
            }))
            state.partnership.containsCreditCard = payMethod.shinhanCardPartnership.containsCreditCard
            state.partnership.containsShinhanPartnershipCard =
                payMethod.shinhanCardPartnership.containsShinhanPartnershipCard

            if (payMethod.billingPayMethods.cardEasyPay) {
                state.cardEasyPay.billing = {
                    pgCp: payMethod.billingPayMethods.cardEasyPay.pgCp,
                    pgId: payMethod.billingPayMethods.cardEasyPay.pgId,
                    minimumAmount: payMethod.billingPayMethods.cardEasyPay.minimumAmount,
                    maximumAmount: payMethod.billingPayMethods.cardEasyPay.maximumAmount,
                }
            }
            if (payMethod.billingPayMethods.card) {
                state.creditCard.billing = {
                    pgCp: payMethod.billingPayMethods.card.pgCp,
                    pgId: payMethod.billingPayMethods.card.pgId,
                    sid: payMethod.billingPayMethods.card.sid,
                    minimumAmount: payMethod.billingPayMethods.card.minimumAmount,
                    maximumAmount: payMethod.billingPayMethods.card.maximumAmount,
                }
            }

            if (payMethod.defaultPayment?.payMethodType === PayMethod.CARD) {
                state.creditCard.selected.companyCode = payMethod.defaultPayment.cardCompanyCode
                    ? (payMethod.defaultPayment.cardCompanyCode as PaymentsCardCodeType)
                    : null
            }
        })
    },
})

export default slice.reducer
